import React, { useCallback, useEffect, useState } from 'react';
import { Edit2, Plug, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { RevenueSource } from '../../types/RevenueSource';
import { getTableFormattedDate } from '../../utils/dateUtils';
import {
  deleteBorkConnection,
  fetchBorkConnection,
  type BorkPosConnection,
} from '../../services/restaurant/restaurantBorkConnectionService';
import SaveBorkConnectionModal from './SaveBorkConnectionModal';

interface Props {
  restaurantId: string;
}

/**
 * The restaurant's own Bork (Trivec) POS connection, shown inside the revenue config card.
 *
 * Only rendered for restaurants that actually have a `trivecgroup` revenue source — that is the
 * source this connection replaces the emailed daily report for, so offering it anywhere else would
 * be meaningless. Everything here is configuration; there is deliberately no enable switch, because
 * the server turns a newly connected venue on by itself.
 */
export const RestaurantBorkConnection: React.FC<Props> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const revenueSources = useRestaurantStore(s => s.restaurantRevenueSources)[restaurantId] || [];
  const hasTrivecgroup = revenueSources.some(rs => rs.revenueSourceName === RevenueSource.TRIVECGROUP);

  const [connection, setConnection] = useState<BorkPosConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Server error text here is untranslated English written for operators, so the user gets a
  // translated line and the detail goes to the console.
  // Memoized on `t` alone: the load effect below depends on it, and an identity that changed every
  // render would make that effect refetch on every render.
  const reportError = useCallback((err: unknown, key: 'loadFailed' | 'disconnectFailed') => {
    console.error('Bork connection error:', err);
    setError(t(`restaurantBorkConnection.error.${key}`));
  }, [t]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      setConnection(await fetchBorkConnection(restaurantId));
    } catch (err) {
      reportError(err, 'loadFailed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasTrivecgroup) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchBorkConnection(restaurantId);
        if (!cancelled) setConnection(result);
      } catch (err) {
        if (!cancelled) reportError(err, 'loadFailed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restaurantId, hasTrivecgroup, reportError]);

  const confirmDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await deleteBorkConnection(restaurantId);
      setConfirmingDisconnect(false);
      await refresh();
    } catch (err) {
      reportError(err, 'disconnectFailed');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!hasTrivecgroup) return null;

  return (
    <div className="mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-700">
      <div className="mb-3 flex items-center gap-2">
        <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wide">
          {t('restaurantBorkConnection.title')}
        </h4>
        {!connection && !loading && (
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 text-xs font-medium transition border border-zinc-200 dark:border-zinc-700"
          >
            <Plug className="w-3.5 h-3.5" />
            {t('restaurantBorkConnection.connect')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">{t('restaurantBorkConnection.loading')}</p>
      ) : !connection ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">{t('restaurantBorkConnection.notConnected')}</p>
      ) : (
        <div className="p-4 rounded-xl flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <p className="font-semibold text-zinc-900 dark:text-zinc-50">
                {t('restaurantBorkConnection.connectedTitle')}
              </p>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  connection.lastRunStatus === 'failure'
                    ? 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                    : 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400'
                }`}
              >
                {connection.lastRunStatus === 'failure'
                  ? t('restaurantBorkConnection.stateNeedsAttention')
                  : t('restaurantBorkConnection.stateActive')}
              </span>
            </div>
            <div className="space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
              <p className="truncate">
                <span className="font-medium">{t('restaurantBorkConnection.posSystem')}:</span>{' '}
                {connection.baseUrl ?? '—'}
              </p>
              <p className="truncate">
                <span className="font-medium">{t('restaurantBorkConnection.accessKey')}:</span>{' '}
                {connection.appIdMasked ?? '—'}
              </p>
              {connection.centerName && (
                <p className="truncate">
                  <span className="font-medium">{t('restaurantBorkConnection.area')}:</span> {connection.centerName}
                </p>
              )}
              <p className="truncate">
                <span className="font-medium">{t('restaurantBorkConnection.lastUpdate')}:</span>{' '}
                {connection.lastRunStatus === 'failure'
                  ? t('restaurantBorkConnection.lastUpdateFailed')
                  : connection.lastRunAt
                  ? getTableFormattedDate(new Date(connection.lastRunAt))
                  : t('restaurantBorkConnection.lastUpdatePending')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:ml-2 self-end sm:self-start">
            <button
              onClick={() => setModalOpen(true)}
              className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition"
              aria-label={t('restaurantBorkConnection.edit')}
            >
              <Edit2 className="w-4 h-4 text-zinc-700 dark:text-zinc-300" />
            </button>
            <button
              onClick={() => setConfirmingDisconnect(true)}
              className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition"
              aria-label={t('restaurantBorkConnection.disconnect')}
            >
              <X className="w-4 h-4 text-red-600 dark:text-red-400" />
            </button>
          </div>
        </div>
      )}

      <SaveBorkConnectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        restaurantId={restaurantId}
        connection={connection}
        onSaved={() => {
          setModalOpen(false);
          refresh();
        }}
      />

      {confirmingDisconnect && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50"
          onClick={() => setConfirmingDisconnect(false)}
        >
          <div
            className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50">
                  {t('restaurantBorkConnection.disconnectTitle')}
                </h2>
                <button
                  onClick={() => setConfirmingDisconnect(false)}
                  className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                  aria-label={t('restaurantBorkConnection.close')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-zinc-600 dark:text-zinc-400">{t('restaurantBorkConnection.disconnectConfirmation')}</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmingDisconnect(false)}
                  disabled={disconnecting}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
                >
                  {t('restaurantBorkConnection.cancel')}
                </button>
                <button
                  onClick={confirmDisconnect}
                  disabled={disconnecting}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-red-600 dark:bg-red-700 text-white dark:text-white hover:bg-red-700 dark:hover:bg-red-800 transition disabled:opacity-50"
                >
                  {disconnecting
                    ? t('restaurantBorkConnection.disconnecting')
                    : t('restaurantBorkConnection.disconnectButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
